/**
 * CSAT custom sending-domain admin API client (SF-4343, epic SF-4337).
 *
 * Wraps the sf-api staff endpoints for a company's custom sending domain so
 * the company edit page can create the record, show the customer's DNS pack,
 * re-check DNS, send a test email and remove the domain. Mirrors the
 * data-access pattern in ../../eva/api/evaBudgetClient.ts.
 *
 *   GET    /admin/csat/sending-domain/:companyId            perm admin:companies:domain:read
 *   POST   /admin/csat/sending-domain/:companyId            perm admin:companies:domain:write
 *          body { domain, from_email, from_name, reply_to }
 *   POST   /admin/csat/sending-domain/:companyId/recheck    perm admin:companies:domain:write  (SF-4341)
 *   POST   /admin/csat/sending-domain/:companyId/test-send  perm admin:companies:domain:write  (SF-4342)
 *          body { to }
 *   DELETE /admin/csat/sending-domain/:companyId            perm admin:companies:domain:write
 *   PATCH  /admin/csat/sending-domain/:companyId/:domainId  perm admin:companies:domain:write
 *          body { from_email?, from_name?, reply_to? } — edit the sender after
 *          setup. The domain itself is immutable (SendGrid/CloudFront
 *          registration): changing it is Remove + re-add.
 *
 * ⚠️ sf-api wraps every response in `{ success, data }`. The Refine
 * `dataProvider.custom` unwraps that envelope and returns `{ data: inner }`,
 * so `useCustom` exposes the record via `query.data?.data`. GET returns 404
 * when the company has no record yet — that is the normal "not configured"
 * state, not an error, so the global toast is suppressed and the section
 * treats a 404 as "no record".
 */
import { useCustom, useCustomMutation } from '@refinedev/core';
import type { AxiosError } from 'axios';

/** Base path for the sending-domain endpoints on sf-api. */
export const CSAT_DOMAIN_BASE = 'admin/csat/sending-domain';
/** List endpoint: every domain a company holds, default first (SF-4350). */
export const CSAT_DOMAINS_LIST_BASE = 'admin/csat/sending-domains';
/** Company field: where unpinned surveys/emails go — the default domain, or the platform. */
export const CSAT_DEFAULT_LINK_HOST_FIELD = 'csat_default_link_host';
export type CsatDefaultLinkHost = 'custom' | 'platform';

/** Permission gating reads (matches sf-api requirePermissions). */
export const CSAT_DOMAIN_READ_PERMISSION = 'admin:companies:domain:read';

/** Permission gating writes — create / recheck / test-send / remove. */
export const CSAT_DOMAIN_WRITE_PERMISSION = 'admin:companies:domain:write';

/** Company field toggled from the edit form (sf-api ADMIN_PATCHABLE_FIELDS). */
export const CSAT_DOMAIN_TOGGLE_FIELD = 'csat_custom_domain_enabled';

export type DnsRecordKind = 'CNAME' | 'TXT' | 'MX';
export type DnsRecordPurpose = 'mail_cname' | 'dkim1' | 'dkim2' | 'web' | 'dmarc' | 'reply_mx';

/** One row of the customer-facing DNS table (sf-api DnsRecord). */
export interface DnsRecord {
  kind: DnsRecordKind;
  host: string;
  value: string;
  /** MX only. */
  priority?: number;
  purpose: DnsRecordPurpose;
  /** null = pending / not checked yet, true = resolves correctly, false = wrong value. */
  valid: boolean | null;
  checked_at?: string | null;
  note?: string;
}

export type SendingDomainStatus =
  | 'draft'
  | 'pending_dns'
  | 'email_ready'
  | 'web_ready'
  | 'live'
  | 'error';

/** The staff view of models/csat/CsatSendingDomain (provider ids included). */
export interface SendingDomain {
  _id?: string;
  company_id: string;
  domain: string;
  from_email: string;
  from_name: string;
  reply_to: string;
  status: SendingDomainStatus;
  /** The company's default domain: used for unpinned sends/links when csat_default_link_host is "custom". */
  is_default?: boolean;
  error_reason?: string | null;
  email?: {
    sendgrid_domain_id?: number | string | null;
    records?: DnsRecord[];
    valid?: boolean;
    validated_at?: string | null;
    error_reason?: string | null;
  };
  web?: {
    cf_tenant_id?: string | null;
    cf_intermediate?: string | null;
    records?: DnsRecord[];
    cert_status?: string;
    valid?: boolean;
    error_reason?: string | null;
  };
  reply?: { records?: DnsRecord[]; valid?: boolean };
  dmarc?: { present?: boolean; records?: DnsRecord[] };
  dns_wait_started_at?: string | null;
  /** Display-ordered DNS table: web, mail_cname, dkim1, dkim2, dmarc (if needed), reply_mx. */
  dns_pack: DnsRecord[];
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Result of POST .../test-send — the `data` of sf-api's envelope, i.e. what
 * csatMailer.send returns. `sent: false` means the API accepted the request
 * but did NOT hand anything to SendGrid (SEND_MAIL is off in that
 * environment). `to` is the address actually used — on non-production
 * environments sf-api redirects to DEV_MAIL, so it can differ from the typed
 * recipient. `mode: 'platform'` means the company's saved toggle is off and
 * the mail went from the shared platform sender, not the custom domain.
 */
export interface TestSendResult {
  sent: boolean;
  mode: 'custom' | 'platform';
  from: string;
  to: string;
  messageId?: string;
}

/**
 * Narrow the unwrapped mutation payload to a TestSendResult. Returns null for
 * anything that is not the documented shape (older API, empty body) so the UI
 * can say "no result reported" instead of inventing a success.
 */
export function parseTestSendResult(payload: unknown): TestSendResult | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  if (typeof p.sent !== 'boolean') return null;
  if (p.mode !== 'custom' && p.mode !== 'platform') return null;
  if (typeof p.from !== 'string' || typeof p.to !== 'string') return null;
  return {
    sent: p.sent,
    mode: p.mode,
    from: p.from,
    to: p.to,
    ...(typeof p.messageId === 'string' && p.messageId ? { messageId: p.messageId } : {}),
  };
}

/**
 * Copy for the test-send outcome, built from the API RESPONSE rather than the
 * typed recipient: `to` may be redirected (DEV_MAIL on non-production),
 * `sent:false` means nothing was handed to SendGrid, and `mode:'platform'`
 * means the custom domain was not exercised at all. "Accepted by SendGrid" is
 * deliberate — the API cannot see inbox delivery.
 */
export interface TestSendOutcome {
  type: 'success' | 'warning' | 'info';
  title: string;
  detail: string;
}

export function describeTestSend(result: TestSendResult | null): TestSendOutcome {
  if (!result) {
    return {
      type: 'info',
      title: 'Test request completed, but the API did not report a send result',
      detail: 'Check the API logs and the mailbox before treating this as a pass.',
    };
  }
  const sender =
    result.mode === 'custom'
      ? `from the custom domain address ${result.from}`
      : `from the PLATFORM address ${result.from} — the custom domain was not used`;
  if (!result.sent) {
    return {
      type: 'warning',
      title: 'Not sent (SEND_MAIL is off in this environment)',
      detail: `Nothing was handed to SendGrid. It would have gone to ${result.to} ${sender}.`,
    };
  }
  const id = result.messageId ? ` SendGrid message id ${result.messageId}.` : '';
  return {
    type: result.mode === 'custom' ? 'success' : 'warning',
    title: `Accepted by SendGrid for ${result.to}`,
    detail: `Sent ${sender}.${id} Accepted is not delivered — check the mailbox and its headers (dkim/spf/dmarc).`,
  };
}

/** Body for POST create. */
export interface CreateSendingDomainBody {
  domain: string;
  from_email: string;
  from_name: string;
  reply_to: string;
}

/**
 * What the create FORM holds, which is not what the API takes: the From address
 * is captured as a local part and the domain is pinned from the `domain` field,
 * so the two can never disagree. `onCreate` composes the body (SF-4417).
 */
export interface CreateDomainFormValues {
  domain: string;
  from_local: string;
  from_name: string;
  reply_to?: string;
}

/** Form values -> API create payload. Domain normalised once, used for both keys. */
export function createBodyFromFormValues(values: CreateDomainFormValues): CreateSendingDomainBody {
  const domain = normalizeDomainInput(values.domain);
  return {
    domain,
    from_email: `${(values.from_local ?? '').trim()}@${domain}`,
    from_name: (values.from_name ?? '').trim(),
    reply_to: (values.reply_to ?? '').trim(),
  };
}

/**
 * Fetch a company's sending-domain record. A 404 means "not configured yet"
 * and is surfaced to the caller via `isError` + status 404 rather than a
 * global toast. Disabled until `companyId` is known and the toggle is on.
 */
export function useSendingDomain(companyId: string, options: { enabled?: boolean } = {}) {
  return useCustom<SendingDomain>({
    url: `/${CSAT_DOMAIN_BASE}/${companyId}`,
    method: 'get',
    queryOptions: {
      enabled: (options.enabled ?? true) && !!companyId,
      refetchOnWindowFocus: false,
      // 404 is an expected state — don't retry it three times before settling.
      retry: false,
    },
    errorNotification: false,
  });
}

/**
 * Every sending domain a company holds, default first then oldest first
 * (SF-4350). An empty list is a normal 200 — there is no 404 "not configured"
 * state on this endpoint.
 */
export function useSendingDomains(companyId: string, options: { enabled?: boolean } = {}) {
  return useCustom<SendingDomain[]>({
    url: `/${CSAT_DOMAINS_LIST_BASE}/${companyId}`,
    method: 'get',
    queryOptions: {
      enabled: (options.enabled ?? true) && !!companyId,
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

/** `?domainId=` selector for the company-keyed routes (omitted → the default). */
function domainQuery(domainId?: string): string {
  return domainId ? `?domainId=${encodeURIComponent(domainId)}` : '';
}

/**
 * Generic mutation hook — the call site supplies url/method/values. The
 * unwrapped payload is a SendingDomain for create/recheck/remove and a
 * TestSendResult for test-send; callers narrow with parseTestSendResult.
 */
export function useSendingDomainMutation() {
  return useCustomMutation<SendingDomain | TestSendResult>();
}

const noToasts = { errorNotification: false as const, successNotification: false as const };

/** POST create args. */
export function createSendingDomainArgs(companyId: string, body: CreateSendingDomainBody) {
  return {
    url: `/${CSAT_DOMAIN_BASE}/${companyId}`,
    method: 'post' as const,
    values: body,
    ...noToasts,
  };
}

/** POST make-default args (SF-4350). */
export function setDefaultSendingDomainArgs(companyId: string, domainId: string) {
  return {
    url: `/${CSAT_DOMAIN_BASE}/${companyId}/${encodeURIComponent(domainId)}/default`,
    method: 'post' as const,
    values: {},
    ...noToasts,
  };
}

/** POST recheck args (endpoint lands in SF-4341). */
export function recheckSendingDomainArgs(companyId: string, domainId?: string) {
  return {
    url: `/${CSAT_DOMAIN_BASE}/${companyId}/recheck${domainQuery(domainId)}`,
    method: 'post' as const,
    values: {},
    ...noToasts,
  };
}

/**
 * POST test-send args (endpoint lands in SF-4342). sf-api resolves the sender
 * from the company's SAVED csat_custom_domain_enabled flag, so the caller must
 * only offer this once that flag is persisted (see canTestSendNow).
 */
export function testSendArgs(companyId: string, to: string, domainId?: string) {
  return {
    url: `/${CSAT_DOMAIN_BASE}/${companyId}/test-send${domainQuery(domainId)}`,
    method: 'post' as const,
    values: { to },
    ...noToasts,
  };
}

/**
 * Sender edit. Only these three fields are accepted by sf-api; `from_email`
 * must stay on the record's domain — the UI edits the local part and renders
 * the domain as a locked suffix so that invariant cannot be broken from here.
 */
export interface UpdateSenderBody {
  from_email?: string;
  from_name?: string;
  /** '' clears the Reply-To. */
  reply_to?: string;
}

/** PATCH sender args. */
export function updateSenderArgs(companyId: string, domainId: string, body: UpdateSenderBody) {
  return {
    url: `/${CSAT_DOMAIN_BASE}/${companyId}/${encodeURIComponent(domainId)}`,
    method: 'patch' as const,
    values: body,
    ...noToasts,
  };
}

/** Split a mailbox into local part + domain; domain is '' when not an email. */
export function splitMailbox(email: string): { local: string; domain: string } {
  const at = email.lastIndexOf('@');
  return at > 0
    ? { local: email.slice(0, at), domain: email.slice(at + 1).toLowerCase() }
    : { local: email, domain: '' };
}

/** Mirror of sf-api's mailbox local-part rule (RFC 5322 dot-atom, ≤ 64 chars). */
const LOCAL_PART_RE = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/i;
export function isValidLocalPart(local: string): boolean {
  const l = local.trim();
  return l.length > 0 && l.length <= 64 && LOCAL_PART_RE.test(l);
}

/**
 * Mirror of sf-api's full mailbox rule (isMailbox in sendingDomainService.js):
 * dot-atom local part <= 64 chars @ a plain hostname. AntD's built-in
 * `type: 'email'` accepts quoted/Unicode/overlong locals the API rejects and
 * rejects a plain `user@host` without a dot — using it for Reply-To let the UI
 * show a value as valid that the API would 400 on (Astra review).
 */
const MAILBOX_HOST_RE =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;
export function isValidMailbox(email: string): boolean {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0) return false;
  const local = trimmed.slice(0, at);
  // Only OUTER whitespace is tolerated (the API trims the whole address). A
  // local part with inner/edge spaces — e.g. "a @example.com" — must fail here,
  // otherwise it passes the client, is sent outer-trimmed only, and the API
  // 400s after Save (Bugbot). isValidLocalPart trims its input, so check first.
  if (local !== local.trim()) return false;
  return isValidLocalPart(local) && MAILBOX_HOST_RE.test(trimmed.slice(at + 1));
}

/** DELETE args. */
export function removeSendingDomainArgs(companyId: string, domainId?: string) {
  return {
    url: `/${CSAT_DOMAIN_BASE}/${companyId}${domainQuery(domainId)}`,
    method: 'delete' as const,
    values: {},
    ...noToasts,
  };
}

/** True when the error is the "no record yet" 404 from GET. */
export function isNotConfigured(error: unknown): boolean {
  return (error as AxiosError | undefined)?.response?.status === 404;
}

/** Map a write failure to readable copy; prefers the server's message. */
export function sendingDomainErrorMessage(error: unknown, fallback: string): string {
  const axiosErr = error as AxiosError<{ message?: string }> | undefined;
  const status = axiosErr?.response?.status;
  const serverMessage = axiosErr?.response?.data?.message;
  if (status === 403)
    return 'You do not have permission to change this company\u2019s sending domain (admin:companies:domain:write).';
  if (status === 404 && !serverMessage) return 'This action is not available yet on the API.';
  return serverMessage ?? fallback;
}

/** Domain part of an email address, lowercased; '' when not an email. */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at > 0
    ? email
        .slice(at + 1)
        .trim()
        .toLowerCase()
    : '';
}

/**
 * Normalise a typed sending domain the way sf-api's normalizeDomain does:
 * trim, lowercase, drop a trailing dot. The create form composes the From
 * address from this, so it must match the `domain` sent in the same payload or
 * the API's FROM_DOMAIN_MISMATCH check fires on a pairing the UI called valid.
 */
export function normalizeDomainInput(domain: string | undefined | null): string {
  return (domain ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
}

/**
 * Mirror of sf-api's HOSTNAME_RE in services/csat/sendingDomainService.js. Kept
 * identical so the form cannot drift from the rule that actually decides the
 * request: validateCreateInput 400s INVALID_DOMAIN on a value this rejects.
 * Two or more labels, each 1-63 chars, no leading or trailing hyphen, 253 total.
 */
const HOSTNAME_RE =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * Is this a hostname the API will accept as a sending domain? Normalises first,
 * so a trailing dot or capitals pass here exactly as they do server-side.
 */
export function isValidSendingHostname(domain: string | undefined | null): boolean {
  return HOSTNAME_RE.test(normalizeDomainInput(domain));
}

/**
 * Does this look like a root domain rather than a subdomain of one? Drives advice
 * only, never a block: a root domain is a legitimate sending domain and the API
 * allows it.
 *
 * Deliberately a label count, not a real answer. `acme.co.uk` is a root domain
 * with three labels and will NOT be flagged, while `csat.acme.co.uk` is a proper
 * subdomain with four and correctly is not. Telling those apart needs a public
 * suffix list, which is out of scope here — this targets the realistic slip of
 * typing a plain `company.com`. Do not promote it to a hard rule.
 */
export function looksLikeRootDomain(domain: string | undefined | null): boolean {
  const d = normalizeDomainInput(domain);
  return isValidSendingHostname(d) && d.split('.').length === 2;
}


/** Mirror of the sf-api invariant: the From address must be ON the sending domain. */
export function fromEmailMatchesDomain(fromEmail: string, domain: string): boolean {
  const d = normalizeDomainInput(domain);
  return !!d && emailDomain(fromEmail) === d;
}

/**
 * Sending is only allowed once the EMAIL half is verified. The two halves
 * (email / survey hosting) are independent: a customer may be web_ready with
 * email never set up, and vice versa. Mirrors csatMailer in sf-api.
 */
export function canTestSend(record: Pick<SendingDomain, 'status' | 'email'> | undefined): boolean {
  if (!record) return false;
  if (record.email && typeof record.email.valid === 'boolean') return record.email.valid;
  return record.status === 'email_ready' || record.status === 'live';
}

/**
 * Test-send is offered only when the email half is verified AND the company's
 * custom-domain toggle is SAVED on. sf-api picks the sender from the persisted
 * flag, so with an unsaved toggle the "test" would go from the platform
 * sender and prove nothing about the custom domain.
 */
export function canTestSendNow(
  record: Pick<SendingDomain, 'status' | 'email'> | undefined,
  savedEnabled: boolean,
): boolean {
  return savedEnabled && canTestSend(record);
}

/** The hosted survey URL is usable once the WEB half is verified. */
export function isHostingLive(record: Pick<SendingDomain, 'status' | 'web'> | undefined): boolean {
  if (!record) return false;
  if (record.web && typeof record.web.valid === 'boolean') return record.web.valid;
  return record.status === 'live' || record.status === 'web_ready';
}

/**
 * Why a half is not verified. Prefer the half's own reason; fall back to the
 * ROW-level error_reason, because a row parked before the halves were split
 * (and any row sf-api fails wholesale) carries the reason on the record only —
 * reading the half alone would render "waiting for DNS" beside an error line.
 * Returns null when that half is verified or nothing is known.
 */
export function halfErrorReason(
  record: Pick<SendingDomain, 'status' | 'error_reason' | 'email' | 'web'> | undefined,
  half: 'email' | 'web',
): string | null {
  if (!record) return null;
  const verified = half === 'email' ? canTestSend(record) : isHostingLive(record);
  if (verified) return null;
  return (
    record[half]?.error_reason ?? (record.status === 'error' ? (record.error_reason ?? null) : null)
  );
}

/**
 * Plain-text three-column table (Type / Host / Value) for pasting into an
 * email to the customer's IT contact. MX rows include the priority in the
 * value column the way DNS providers expect it.
 */
/** The value as a customer should paste it — MX rows carry their priority. */
export function displayValue(r: DnsRecord): string {
  return r.kind === 'MX' && r.priority != null ? `${r.priority} ${r.value}` : r.value;
}

/** Human label per row purpose — shared by the on-screen table and the copy output. */
export const DNS_PURPOSE_LABEL: Record<DnsRecordPurpose, string> = {
  web: 'Survey website',
  mail_cname: 'Email bounce / SPF',
  dkim1: 'Email signing key 1',
  dkim2: 'Email signing key 2',
  dmarc: 'Email policy (only if none exists)',
  reply_mx: 'Replies (future helpdesk use)',
};

/**
 * Plain-text DNS pack for pasting into an email to the customer's IT team.
 * Pipe-delimited (Markdown-style) rather than space-padded: a padded table
 * only lines up in a monospace font, and mail clients are proportional, so the
 * columns drifted. Pipes survive any font, and Slack/Teams/GitHub render them
 * as a real table.
 */
export function dnsPackAsText(records: DnsRecord[]): string {
  const header = ['Purpose', 'Type', 'Host', 'Value'];
  const rows = records.map((r) => [
    DNS_PURPOSE_LABEL[r.purpose] ?? r.purpose,
    r.kind,
    r.host,
    displayValue(r),
  ]);
  const line = (cells: string[]) => `| ${cells.join(' | ')} |`;
  return [line(header), line(header.map(() => '---')), ...rows.map(line)].join('\n');
}

const escapeHtml = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The same pack as an HTML table. Written alongside the text version so a
 * rich-text paste (Outlook, Gmail, Apple Mail) gets a real bordered table with
 * monospace host/value cells, while plain-text targets fall back to the pipes.
 */
export function dnsPackAsHtml(records: DnsRecord[]): string {
  const th = (t: string) =>
    `<th style="text-align:left;padding:4px 8px;border:1px solid #ccc;background:#f5f5f5">${escapeHtml(t)}</th>`;
  const td = (t: string, mono = false) =>
    `<td style="padding:4px 8px;border:1px solid #ccc${mono ? ';font-family:monospace' : ''}">${escapeHtml(t)}</td>`;
  const rows = records
    .map(
      (r) =>
        `<tr>${td(DNS_PURPOSE_LABEL[r.purpose] ?? r.purpose)}${td(r.kind)}${td(r.host, true)}${td(displayValue(r), true)}</tr>`,
    )
    .join('');
  return `<table style="border-collapse:collapse;font-size:13px"><thead><tr>${th('Purpose')}${th('Type')}${th('Host')}${th('Value')}</tr></thead><tbody>${rows}</tbody></table>`;
}
